import { observer } from 'mobx-react';
import { PropTypes } from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { Alert, Col, Image, Row, Spinner } from 'react-bootstrap';
import { storesContext } from '../../../stores/storesContext';
import SummaryMap from '../maps/SummaryMap';
import IrrimaxSiteButton from './IrrimaxSiteButton';
import IrrimaxSiteLayout from './IrrimaxSiteLayout';

const IrrimaxSite = ({ site, siteSummaries, selectSite }) => {
  const userStore = useContext(storesContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [graphs, setGraphs] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      if (mounted) setLoading(true);
      try {
        const url = `${process.env.REACT_APP_PROPERTIES_API_URL}api/irrigation-monitoring-sites/${site.id}/irrimax-sites/graphs`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${userStore.bearerToken}`,
            Accept: 'image/png',
            SeasonalDatabase: userStore.selectedDatabase
          }
        });
        if (!response.ok) {
          if (mounted) setError('Failed to fetch graphs.');
        } else if (response.status === 401) {
          userStore.refresh();
        }
        const images = await response.json();
        if (mounted) setGraphs(images);
      } catch (error) {
        if (mounted) setError('An error occured when fetching the graphs.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [
    site.id,
    site.irrimax_sites,
    userStore,
    userStore.bearerToken,
    userStore.selectedDatabase
  ]);

  if (loading) {
    return (
      <IrrimaxSiteLayout>
        <Spinner className='mt-1' animation='border' variant='primary' />
      </IrrimaxSiteLayout>
    );
  }

  if (error) {
    return (
      <IrrimaxSiteLayout>
        <Row>
          <Col sm={2}></Col>
          <Col sm={8}>
            <Alert variant='danger'>{error}</Alert>
          </Col>
          <Col sm={2}></Col>
        </Row>
      </IrrimaxSiteLayout>
    );
  }

  if (graphs.length === 0) {
    return (
      <IrrimaxSiteLayout>
        <Row>
          <Col sm={2}></Col>
          <Col sm={8}>
            <Alert variant='danger'>No graphs found for this site.</Alert>
          </Col>
          <Col sm={2}></Col>
        </Row>
      </IrrimaxSiteLayout>
    );
  }

  return (
    <IrrimaxSiteLayout>
      {graphs.map((graph, index) => {
        const graphUrl = site.irrimax_sites.find(
          (is) => is.id === graph.irrimax_site_id
        )?.graph_url;
        return (
          <Row key={graphUrl} className='mb-3'>
            <Col>
              <Row className='mb-2'>
                <Col>
                  <Image
                    key={index}
                    src={`data:image/png;base64,${graph.graph}`}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <IrrimaxSiteButton site={site} graphUrl={graphUrl} />
                </Col>
              </Row>
            </Col>
          </Row>
        );
      })}
      <Row style={{ height: '500px', width: '100%' }}>
        <Col>
          <SummaryMap
            propertySummaries={[
              {
                loading: false,
                property: userStore.propertiesStore.selectedProperty,
                results: {
                  sites: siteSummaries
                }
              }
            ]}
            selectSite={selectSite}
            selectedSite={site}
          />
        </Col>
      </Row>
    </IrrimaxSiteLayout>
  );
};

IrrimaxSite.propTypes = {
  site: PropTypes.object.isRequired
};

export default observer(IrrimaxSite);

